import { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Award, BookOpen, Download, Eye, Printer } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TourQuiz } from "@/components/TourQuiz";
import { downloadCertificate } from "@/lib/certificate";
import { api, errMsg, fetcher } from "@/lib/api";

export default function Documentacion() {
  const { data } = useSWR("/docs", fetcher);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState("");
  const [quiz, setQuiz] = useState(false);

  const open = async (doc) => {
    setBusy(doc.slug);
    try {
      const { data: html } = await api.get(`/docs/${doc.slug}/html`, { responseType: "text" });
      setView({ ...doc, html });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const download = async (doc) => {
    setBusy(`pdf-${doc.slug}`);
    try {
      const { data: blob } = await api.get(`/docs/${doc.slug}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.slug}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Documento descargado");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const items = data?.items || [];
  const booklet = items.find((d) => d.booklet);
  const manuals = items.filter((d) => !d.booklet);

  return (
    <div data-testid="documentacion-page">
      <PageHeader
        kicker="Documentación"
        title="Manuales del sistema"
        description="Diseño técnico, administración y operación, con los flujos de trabajo y la marca de la agencia."
      >
        <Button variant="outline" onClick={() => downloadCertificate()}
                data-testid="my-certificate-btn">
          <Download className="h-4 w-4 mr-2" />Mi certificado
        </Button>
        <Button variant="outline" onClick={() => setQuiz(true)} data-testid="open-quiz-btn">
          <Award className="h-4 w-4 mr-2" />Evaluación interna
        </Button>
      </PageHeader>

      {booklet && (
        <div className="border border-primary/40 bg-primary/5 p-6 mb-6 flex flex-col md:flex-row md:items-center gap-5"
             data-testid="booklet-card">
          <Printer className="h-7 w-7 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="text-base md:text-lg font-semibold">{booklet.title}</h2>
            <p className="text-sm text-muted-foreground mt-1">{booklet.summary}</p>
            <p className="text-xs text-muted-foreground mt-2">
              {manuals.length} documentos · {booklet.words} palabras · versión {booklet.version}
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" onClick={() => open(booklet)}
                    disabled={busy === booklet.slug} data-testid="booklet-view-btn">
              <Eye className="h-3.5 w-3.5 mr-1.5" />
              {busy === booklet.slug ? "Armando…" : "Ver"}
            </Button>
            <Button onClick={() => download(booklet)} disabled={busy === `pdf-${booklet.slug}`}
                    data-testid="booklet-pdf-btn">
              <Download className="h-3.5 w-3.5 mr-1.5" />
              {busy === `pdf-${booklet.slug}` ? "Generando…" : "PDF para imprimir"}
            </Button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-5" data-testid="docs-grid">
        {manuals.map((doc) => (
          <div key={doc.slug} className="border border-border bg-card p-6 flex flex-col"
               data-testid={`doc-card-${doc.slug}`}>
            <BookOpen className="h-5 w-5 text-primary mb-3" />
            <h2 className="text-base md:text-lg font-semibold leading-snug">{doc.title}</h2>
            <Badge variant="secondary" className="w-fit mt-2 text-[10px]">{doc.audience}</Badge>
            <p className="text-sm text-muted-foreground mt-3 flex-1">{doc.summary}</p>
            <p className="text-xs text-muted-foreground mt-3">{doc.words} palabras</p>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => open(doc)}
                      disabled={busy === doc.slug} data-testid={`doc-view-${doc.slug}`}>
                <Eye className="h-3.5 w-3.5 mr-1.5" />Ver
              </Button>
              <Button size="sm" onClick={() => download(doc)}
                      disabled={busy === `pdf-${doc.slug}`} data-testid={`doc-pdf-${doc.slug}`}>
                <Download className="h-3.5 w-3.5 mr-1.5" />
                {busy === `pdf-${doc.slug}` ? "Generando…" : "PDF"}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-4xl" data-testid="doc-view-dialog">
          <DialogHeader><DialogTitle>{view?.title}</DialogTitle></DialogHeader>
          <iframe title={view?.title || "documento"} srcDoc={view?.html || ""}
                  className="w-full h-[70vh] border border-border bg-white"
                  data-testid="doc-view-frame" />
        </DialogContent>
      </Dialog>

      {quiz && <TourQuiz open={quiz} onClose={() => setQuiz(false)} />}
    </div>
  );
}
